import React, { useEffect, useState } from "react";
import { Plus, Pencil, Copy, KeyRound } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState } from "@/components/ListState";
import { RoleBadge, ROLE_LABELS, DotLabel } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const SettingsUsers = () => {
  const { canWrite, user: me } = useAuth();
  const writable = canWrite();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [tempPwOpen, setTempPwOpen] = useState(false);
  const [tempPw, setTempPw] = useState("");
  const [form, setForm] = useState({ name: "", email: "", role: "td" });
  const [editUser, setEditUser] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      setLoadError(getErrorMessage(err, "Gebruikers konden niet geladen worden."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ name: "", email: "", role: "td" });
    setError("");
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    setError("");
    try {
      const { data } = await api.post("/users", form);
      setCreateOpen(false);
      setTempPw(data.temp_password);
      setTempPwOpen(true);
      await load();
    } catch (err) {
      setError(getErrorMessage(err, "Aanmaken mislukt."));
    }
  };

  const openEdit = (u) => {
    setEditUser({ ...u });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    try {
      await api.put(`/users/${editUser.id}`, {
        name: editUser.name,
        role: editUser.role,
      });
      toast.success("Gebruiker bijgewerkt");
      setEditOpen(false);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Bijwerken mislukt."));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Gebruiker gedeactiveerd" : "Gebruiker geactiveerd");
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Actie mislukt."));
    }
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setTempPw(data.temp_password);
      setTempPwOpen(true);
    } catch (err) {
      toast.error(getErrorMessage(err, "Reset mislukt."));
    }
  };

  const copyTemp = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(tempPw);
      toast.success("Wachtwoord gekopieerd");
    } catch (e) {
      toast.error("Kopiëren mislukt - selecteer en kopieer het wachtwoord handmatig.");
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-body text-muted-foreground">{users.length} gebruikers</p>
        {writable && (
          <Button data-testid="users-create-button" onClick={openCreate}>
            <Plus size={18} className="mr-2" /> Gebruiker aanmaken
          </Button>
        )}
      </div>

      <Card>
        {loadError ? (
          <ErrorState testid="users-load-error" message={loadError} onRetry={load} className="border-0" />
        ) : (
        <>
        <div className="hidden sm:block">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[200px] text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-body text-muted-foreground">Laden...</TableCell></TableRow>
              )}
              {!loading && users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-foreground">{u.name}</TableCell>
                  <TableCell className="text-body">{u.email}</TableCell>
                  <TableCell><RoleBadge role={u.role} /></TableCell>
                  <TableCell>
                    <DotLabel tone={u.active ? "groen" : "neutraal"} word>
                      {u.active ? "Actief" : "Gedeactiveerd"}
                    </DotLabel>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {writable ? (
                        <>
                          <Button variant="ghost" size="icon" data-testid={`user-row-edit-button-${u.email}`} onClick={() => openEdit(u)} title="Bewerken">
                            <Pencil size={18} />
                          </Button>
                          <Button variant="ghost" size="icon" data-testid={`user-row-reset-button-${u.email}`} onClick={() => resetPassword(u)} title="Wachtwoord resetten">
                            <KeyRound size={18} />
                          </Button>
                          <div className="flex items-center gap-1 pl-1">
                            <Switch
                              data-testid={`user-row-active-switch-${u.email}`}
                              checked={u.active}
                              disabled={u.id === me?.id}
                              onCheckedChange={() => toggleActive(u)}
                            />
                          </div>
                        </>
                      ) : (
                        <span className="text-data text-muted-foreground">Geen rechten</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile: stacked cards (naam → e-mail → rol/status + acties). */}
        <div className="divide-y divide-border sm:hidden" data-testid="users-cardlist">
          {loading && <p className="py-8 text-center text-body text-muted-foreground">Laden...</p>}
          {!loading && users.map((u) => (
            <div key={u.id} data-testid={`user-card-${u.email}`} className="flex items-start justify-between gap-3 p-3">
              <div className="min-w-0 space-y-0.5">
                <div className="truncate font-medium text-foreground">{u.name}</div>
                <div className="truncate text-data text-muted-foreground">{u.email}</div>
                <div className="flex flex-wrap items-center gap-1 pt-1">
                  <RoleBadge role={u.role} />
                  <DotLabel tone={u.active ? "groen" : "neutraal"} word>
                    {u.active ? "Actief" : "Gedeactiveerd"}
                  </DotLabel>
                </div>
              </div>
              {writable ? (
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="icon" data-testid={`user-card-edit-button-${u.email}`} onClick={() => openEdit(u)} title="Bewerken"><Pencil size={18} /></Button>
                  <Button variant="ghost" size="icon" data-testid={`user-card-reset-button-${u.email}`} onClick={() => resetPassword(u)} title="Wachtwoord resetten"><KeyRound size={18} /></Button>
                  <Switch data-testid={`user-card-active-switch-${u.email}`} checked={u.active} disabled={u.id === me?.id} onCheckedChange={() => toggleActive(u)} />
                </div>
              ) : (
                <span className="shrink-0 text-data text-muted-foreground">Geen rechten</span>
              )}
            </div>
          ))}
        </div>
        </>
        )}
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent srDescription="Maak een gebruiker aan. Je krijgt daarna eenmalig een tijdelijk wachtwoord te zien.">
          <DialogHeader><DialogTitle>Gebruiker aanmaken</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-name">Naam</Label>
              <Input id="c-name" data-testid="user-create-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-email">E-mailadres</Label>
              <Input id="c-email" type="email" data-testid="user-create-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-create-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">{ROLE_LABELS.owner}</SelectItem>
                  <SelectItem value="td">{ROLE_LABELS.td}</SelectItem>
                  <SelectItem value="management">{ROLE_LABELS.management}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <p className="text-data text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>Annuleren</Button>
            <Button data-testid="user-create-submit-button" onClick={submitCreate}>Aanmaken</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent srDescription="Pas naam, rol en toegang van deze gebruiker aan.">
          <DialogHeader><DialogTitle>Gebruiker bewerken</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="e-name">Naam</Label>
                <Input id="e-name" data-testid="user-edit-name-input" value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>E-mailadres</Label>
                <Input value={editUser.email} disabled className="bg-muted/40" />
              </div>
              <div className="space-y-1.5">
                <Label>Rol</Label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger data-testid="user-edit-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">{ROLE_LABELS.owner}</SelectItem>
                    <SelectItem value="td">{ROLE_LABELS.td}</SelectItem>
                    <SelectItem value="management">{ROLE_LABELS.management}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditOpen(false)}>Annuleren</Button>
            <Button data-testid="user-edit-submit-button" onClick={submitEdit}>Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password dialog */}
      <Dialog open={tempPwOpen} onOpenChange={setTempPwOpen}>
        <DialogContent data-testid="temp-password-dialog">
          <DialogHeader><DialogTitle>Tijdelijk wachtwoord</DialogTitle></DialogHeader>
          <DialogDescription>
            Kopieer dit wachtwoord nu - het wordt maar &eacute;&eacute;n keer getoond. De gebruiker moet het bij de eerste keer inloggen wijzigen.
          </DialogDescription>
          <div className="flex items-center gap-2">
            <Input readOnly value={tempPw} className="font-mono" />
            <Button data-testid="temp-password-copy-button" onClick={copyTemp}>
              <Copy size={18} className="mr-2" /> Kopieer
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPwOpen(false)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
